import Immutable from "immutable";
import React, { useEffect, useRef, useState } from "react";
import useFetchSuggestions from "../../../../common/choices/useFetchSuggestions";
import useNamesForCodes from "../../../../common/choices/useNamesForCodes";
import { useCloseDropdownEvents } from "../../../../common/components/useCloseDropdownEvents";
import { isEmptyObject } from "../../../../search/utils";
import Suggestions, { suggestionsType } from "./Suggestions";
import { eventInputType, eventKeyboardEventType } from "../../../../common/types/common";

interface AutoSuggestProps {
	id: string;
	currentInput?: string;
	endpoint: string;
	getSuggestions?: (options: unknown, value: string) => unknown;
	onInputChange: (key: string, value: string) => void;
	label?: string;
	params?: { [key: string]: string };
	placeholder?: string;
	searchByCode?: boolean;
}

export default function AutoSuggest({
	placeholder,
	id,
	currentInput = "",
	onInputChange,
	endpoint,
	searchByCode = true,
	params = {},
}: AutoSuggestProps) {
	const ref = useRef(null);
	const codeFetchKey = "code";
	const codeFetchParameter = "codes";
	const { nameIndex } = useNamesForCodes(
		endpoint,
		codeFetchKey,
		codeFetchParameter,
		Immutable.List(
			currentInput && currentInput.length > 0 ? [currentInput] : [],
		),
		searchByCode,
	);
	const [dropdownIsOpened, setDropdownIsOpened] = useState(false);
	useCloseDropdownEvents(ref, dropdownIsOpened, () =>
		setDropdownIsOpened(!dropdownIsOpened),
	);
	const [value, setValue] = useState("");
	const [suggestions, setSuggestions] = useState<suggestionsType>([]);
	const [fetchParams, setFetchParams] = useState(params);
	const [cursor, setCursor] = useState(-1);
	const highlightedItem = suggestions[cursor];

	const shouldFetch = !isEmptyObject(fetchParams) && value.length > 1;

	const { choices, loading, error } = useFetchSuggestions(
		endpoint,
		fetchParams,
		shouldFetch,
		500,
	);

	useEffect(() => {
		if (!loading && !error && choices && value.length > 1) {
			setSuggestions(
				choices.map((item: { code: string; name: string }) => ({
					...item,
					disabled: currentInput === item.code,
				})),
			);
		}
	}, [loading, error, choices, value]);

	useEffect(() => {
		if (!nameIndex.isEmpty()) {
			setValue(nameIndex.get(currentInput, currentInput + "..."));
		}
	}, [nameIndex]);

	useEffect(() => {
		if (!isEmptyObject(params)) {
			setFetchParams({
				...fetchParams,
				...params,
			});
		}
	}, [params]);

	const handleChange = (e: eventInputType) => {
		const value = e.target.value;

		setFetchParams({
			...params,
			[codeFetchParameter]: [],
			query: value,
		});

		setDropdownIsOpened(true);
		setValue(value);

		if (!value) {
			setSuggestions([]);
		}
	};

	const handleSuggestionSelected = (key: string, name: string) => {
		if (currentInput === key) {
			return;
		}

		onInputChange(key, name);
		setDropdownIsOpened(false);
		setCursor(-1);
		setSuggestions([]);
		setValue(name);
	};

	const handleKeyDown = (e: eventKeyboardEventType) => {
		if (e.key === "ArrowUp" && cursor > 0) {
			setCursor(cursor - 1);
		} else if (e.key === "ArrowDown" && cursor < suggestions.length - 1) {
			setCursor(cursor + 1);
		}

		if (e.key === "Enter") {
			e.preventDefault();
			setDropdownIsOpened(!dropdownIsOpened);

			if (highlightedItem) {
				const { code, name } = highlightedItem;
				handleSuggestionSelected(code, name);
			}
		}
	};

	const handleDropdownToggleClick = () => {
		setDropdownIsOpened(true);
	};

	return (
		<div ref={ref} className="criterion input-field autosuggest-container">
			<input
				type="text"
				className="form-control"
				id={id}
				name={id}
				value={value}
				placeholder={placeholder}
				autoComplete="off"
				onFocus={handleDropdownToggleClick}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
			/>

			<Suggestions
				value={value}
				suggestions={suggestions}
				dropdownIsOpened={dropdownIsOpened}
				onSuggestionSelected={handleSuggestionSelected}
				active={highlightedItem?.code}
				loading={loading}
			/>
		</div>
	);
}
